import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { auth, db, doc, getDoc, onAuthStateChanged, signOut } from "./firebase.js";
import { clearUnlock } from "./apps/financials/lib/finSession.js";
import { dataService } from "./lib/dataService.js";
import { bootstrapUser } from "./lib/bootstrapUser.js";
import { loadEquipmentNames } from "./lib/equipment.js";
import { isEventUser, loadProfile } from "./lib/eventAccount.js";
import { C, font } from "./lib/styles.js";
import { Notifications } from "./lib/notify.jsx";
import { getApp } from "./lib/appRegistry.js";
import { visibleJobsNav } from "./apps/jobs/lib/jobAccess.js";
import LoginScreen, { SignInCheckFailed } from "./components/LoginScreen.jsx";
import AuthenticatorSetup from "./components/AuthenticatorSetup.jsx";
import { checkSession } from "./lib/authFlow.js";
import { ENROLLED_SIGN_IN_AGAIN_TEXT } from "./lib/passwordSignIn.js";
import IdleSignOut, { useIdleMinutes } from "./components/IdleSignOut.jsx";
import { idleSignedOutText } from "./lib/idleSignOut.js";
import Homepage from "./components/Homepage.jsx";
import AppTopbar from "./components/AppTopbar.jsx";
import WebsiteApp from "./apps/website/WebsiteApp.jsx";
import JobsApp from "./apps/jobs/JobsApp.jsx";
import IngestApp from "./apps/ingest/IngestApp.jsx";
import SafeShareApp from "./apps/safeshare/SafeShareApp.jsx";
import TrafficApp from "./apps/traffic/TrafficApp.jsx";
import SheetsApp from "./apps/sheets/SheetsApp.jsx";
import AutocycleApp from "./apps/autocycle/AutocycleApp.jsx";
import CrmApp from "./apps/crm/CrmApp.jsx";
import { SEARCH_EVENT } from "./apps/crm/SearchPalette.jsx";
import { __clearCrmCaches as clearAllInteractionsCache } from "./apps/crm/useAllInteractions.js";
import { __clearCrmCaches as clearOpenItemsCache } from "./apps/crm/useOpenItems.js";
import { clearIngestListCache } from "./lib/ingestList.js";
import FinancialsApp from "./apps/financials/FinancialsApp.jsx";
import LastCaptureDisplay, { displayRouteFromLocation } from "./apps/ingest/views/rigv2/LastCaptureDisplay.jsx";
import FeedbackTab, { openFeedback } from "./components/FeedbackTab.jsx";
import { parseGoParams, setPendingJob, stripGoParams } from "./lib/feedback/deepLink.js";
import { feedbackApi } from "./lib/feedback/feedbackCallables.js";
import { plannerStatus } from "./lib/feedback/model.js";
import { clearErrors } from "./lib/feedback/errorBuffer.js";

// The account signed in right now, for the Feedback tab: a send that started
// for one account never goes out after another has signed in (another tab).
const currentAccount = () => auth.currentUser?.uid ?? null;

export default function App() {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // The sign-in check (docs/ops/password-signin.md), for ONE account: { status,
  // uid }. After every sign-in, before anything is read. "checking" until
  // checkSession answers; "ok" lets the user lookup below run; "enroll" shows
  // only the authenticator set-up; "failed" (the session could not be read)
  // shows only Try again / Sign out. Everything below acts only when the
  // check is for the account signed in now (gate.uid), so nothing approved
  // for one account ever carries over to the next.
  const [gate, setGate] = useState({ status: "idle", uid: null });
  const [gateRun, setGateRun] = useState(0);
  const justEnrolled = useRef(false);
  const [activeApp, setActiveApp] = useState(null);
  const [activeNav, setActiveNav] = useState(0);
  const [feedbackCount, setFeedbackCount] = useState({ open: 0, bugs: 0 });
  // Bumped on every nav click, even when the same tab is clicked — lets apps
  // reset drill-down state (e.g. Jobs detail view → back to Jobs list).
  const [navClickTick, setNavClickTick] = useState(0);
  const handleNavChange = useCallback((i) => {
    setActiveNav(i);
    setNavClickTick(t => t + 1);
  }, []);

  // ?convert=<quoteSubmissions id> — deep link from the inquiry email (and the
  // Prospects panel) that opens the Quote Builder pre-filled from a website
  // submission. The param is captured once and stripped from the URL; it also
  // rides in sessionStorage so a reload before sign-in completes (e.g. blocked
  // popup) doesn't lose the intent. Cleared when the conversion resolves.
  const [pendingConvertId, setPendingConvertId] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("convert");
    if (p) {
      window.history.replaceState({}, "", window.location.pathname);
      try { sessionStorage.setItem("nycap-pending-convert", p); } catch { /* private mode */ }
      return p;
    }
    try { return sessionStorage.getItem("nycap-pending-convert") || null; } catch { return null; }
  });
  const consumeConvert = useCallback(() => {
    setPendingConvertId(null);
    try { sessionStorage.removeItem("nycap-pending-convert"); } catch { /* private mode */ }
  }, []);

  // /qb-callback — Intuit's OAuth redirect (?code=&realmId=&state= or
  // ?error=access_denied). Captured once with the URL scrubbed BEFORE first
  // paint (?convert= precedent). Held in React state only — never mirrored to
  // storage: the code is single-use secret-ish material; if auth somehow
  // doesn't survive the round-trip, the user just clicks Connect again.
  const [pendingQboCallback, setPendingQboCallback] = useState(() => {
    if (window.location.pathname !== "/qb-callback") return null;
    const p = new URLSearchParams(window.location.search);
    const cb = {
      code: p.get("code"), realmId: p.get("realmId"),
      state: p.get("state"), error: p.get("error"),
    };
    window.history.replaceState({}, "", "/");
    return (cb.code && cb.state && cb.realmId) || cb.error ? cb : null;
  });
  const consumeQboCallback = useCallback(() => setPendingQboCallback(null), []);

  // ?go=<app>&tab=<label>&job=<id> — "Open this page" from the feedback
  // inbox (src/lib/feedback/deepLink.js). Read once on the home path before
  // first paint and taken out of the address bar (?convert= precedent);
  // applied after sign-in below, only to an app and tab this person has.
  const [pendingGo, setPendingGo] = useState(() => {
    if (window.location.pathname !== "/") return null;
    const go = parseGoParams(window.location.search);
    stripGoParams();
    return go;
  });

  // /display?job=<jobId> — the second display (ingest S8): the Rig tab opens
  // this path as its own window on the monitor next to the capture station.
  // Read once before first paint (?convert= precedent). The URL is kept as
  // is: a reload of that window, or the browser restoring it, keeps the job,
  // and a job id is not a secret. Sign-in is untouched; once signed in the
  // window renders the display in place of the shell (below).
  const [displayRoute] = useState(() => displayRouteFromLocation(window.location));

  // An event account (docs/ops/event-role.md): Ingest only. It opens there,
  // and no other app, deep link or business read runs for it.
  const eventAccount = isEventUser(appUser);

  // Once signed in with a pending QBO callback, jump to Financials → Settings
  // (its Settings view completes the exchange, re-prompting the passphrase
  // first if the unlock didn't survive the round-trip).
  useEffect(() => {
    if (!appUser?._docId || !pendingQboCallback) return;
    if (eventAccount) { setPendingQboCallback(null); return; } // Ingest only
    const finNav = getApp("financials")?.navItems || [];
    setActiveApp("financials");
    setActiveNav(Math.max(0, finNav.indexOf("Settings")));
  }, [appUser?._docId, pendingQboCallback, eventAccount]);

  // Once signed in with a pending ?go= link, open that app and tab, if this
  // person may (their apps list; never for an event account), and hand Jobs
  // or Ingest the job to open. A ?convert= or QuickBooks return wins.
  useEffect(() => {
    if (!appUser?._docId || !pendingGo) return;
    setPendingGo(null);
    if (eventAccount || pendingConvertId || pendingQboCallback) return;
    const app = getApp(pendingGo.app);
    if (!app || !(appUser.apps || []).includes(app.id)) return;
    const nav = app.id === "jobs" ? visibleJobsNav(app.navItems, appUser.role) : app.navItems;
    const tabIdx = pendingGo.tab ? nav.indexOf(pendingGo.tab) : -1;
    setPendingJob(app.id, pendingGo.job);
    setActiveApp(app.id);
    setActiveNav(Math.max(0, tabIdx));
  }, [appUser, pendingGo, eventAccount, pendingConvertId, pendingQboCallback]);

  // Once signed in with a pending convert, jump to Jobs → Quotes. JobsApp
  // fetches the submission and loads the builder, then calls consumeConvert.
  const appUserId = appUser?._docId;
  const appUserRole = appUser?.role;
  useEffect(() => {
    if (eventAccount) setActiveApp("ingest");
  }, [eventAccount, appUserId]);
  useEffect(() => {
    if (!appUserId || !pendingConvertId) return;
    if (eventAccount) { setPendingConvertId(null); return; }
    const jobsNav = visibleJobsNav(getApp("jobs")?.navItems || [], appUserRole);
    const quotesIdx = jobsNav.indexOf("Quotes");
    if (quotesIdx < 0) { setPendingConvertId(null); return; } // role can't use Quotes
    setActiveApp("jobs");
    setActiveNav(quotesIdx);
  }, [appUserId, appUserRole, pendingConvertId, eventAccount]);

  // The nav labels actually shown for the active app + this user. Jobs hides the
  // Quotes tab from non-admins; every other app shows its full registry nav. The
  // SAME array feeds the topbar AND the app below, so a filtered tab can never
  // shift positions and misroute clicks. activeNav indexes into THIS list.
  const visibleNav = useMemo(() => {
    if (!activeApp) return [];
    const app = getApp(activeApp);
    if (!app) return [];
    return activeApp === "jobs" ? visibleJobsNav(app.navItems, appUser?.role) : app.navItems;
  }, [activeApp, appUser]);

  // What this tab holds for the account signed in (the Financials unlock, the
  // CRM's cached collections, the open app, the badge, a pending deep link),
  // dropped on sign-out and on any change of account. Local only: it never
  // signs anyone out of Firebase.
  const resetLocalSession = useCallback(() => {
    clearUnlock();
    /* The CRM's two whole-collection reads are session-cached in module
       scope, and `interactions` holds email bodies. React state goes with the
       unmount; those caches would not, so they are dropped by name. */
    clearAllInteractionsCache();
    clearOpenItemsCache();
    clearIngestListCache(); // Ingest's job list belongs to the account that read it
    setActiveApp(null);
    setActiveNav(0);
    setFeedbackCount({ open: 0, bugs: 0 });
    setPendingQboCallback(null);
    setPendingConvertId(null);
    setPendingGo(null);
    setPendingJob(null, null);
    clearErrors(); // the Feedback tab's recent errors belong to the account that had them
    try { sessionStorage.removeItem("nycap-pending-convert"); } catch { /* private mode */ }
  }, []);
  const lastUid = useRef(null);

  // 1. Firebase auth state listener. Any change of account (another tab
  // signing in as someone else included) drops the last account's profile,
  // approval and local session in the same update, before anything renders
  // or reads. The first report after a page load (no account before) keeps
  // what the page was opened with (a pending deep link, the Financials unlock
  // that must survive the QuickBooks round trip).
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      const uid = u?.uid ?? null;
      if (lastUid.current !== null && lastUid.current !== uid) resetLocalSession();
      lastUid.current = uid;
      justEnrolled.current = false;
      setFirebaseUser(u);
      setAppUser(null);
      setGate({ status: u ? "checking" : "idle", uid: u?.uid ?? null });
      setLoading(Boolean(u));
    });
    return unsub;
  }, [resetLocalSession]);

  // 1b. The password sign-in check. A password sign-in without the
  // authenticator code, while the switch says "required", gets the set-up
  // screen (or, if the account already has an authenticator, is signed out to
  // sign in again with the code); an unconfirmed email is signed out. Nothing
  // below runs until this says "ok". Re-run after a set-up (gateRun).
  useEffect(() => {
    if (!firebaseUser) return;
    const uid = firebaseUser.uid;
    let cancelled = false;
    // A check that answers after another account has signed in (in another
    // tab) is about the old account: it must not change anything, above all
    // not sign the new account out. Checked against the current Firebase user,
    // which changes before React catches up.
    const stillMine = () => !cancelled && auth.currentUser?.uid === uid;
    setGate({ status: "checking", uid });
    setLoading(true);
    (async () => {
      let result;
      try {
        result = await checkSession(firebaseUser);
      } catch (e) {
        console.warn("[Auth] session check failed:", typeof e?.code === "string" ? e.code : "unknown");
        if (stillMine()) {
          setGate({ status: "failed", uid });
          setLoading(false);
        }
        return;
      }
      if (!stillMine()) return;
      if (result.status === "ok") {
        justEnrolled.current = false;
        setGate({ status: "ok", uid });
        return;
      }
      if (result.status === "enroll") {
        setGate({ status: "enroll", uid });
        setLoading(false);
        return;
      }
      // "sign-in-again" or "unverified": out, with the reason on the sign-in screen.
      setAuthError(justEnrolled.current && result.status === "sign-in-again" ? ENROLLED_SIGN_IN_AGAIN_TEXT : result.message);
      justEnrolled.current = false;
      setGate({ status: "idle", uid });
      await signOut(auth);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [firebaseUser, gateRun]);

  // The account whose sign-in check passed, if it is the one signed in now.
  const approvedUid = firebaseUser && gate.status === "ok" && gate.uid === firebaseUser.uid ? gate.uid : null;
  const gateStatus = firebaseUser && gate.uid === firebaseUser.uid ? gate.status : "checking";

  // Errors from the sign-in screens are not part of this account's session:
  // the Feedback tab's buffer starts empty once the sign-in check passes.
  useEffect(() => {
    if (approvedUid) clearErrors();
  }, [approvedUid]);

  // 2. Resolve app user from new `users` collection when Firebase user changes
  // (only once the sign-in check above says "ok" for this very account).
  useEffect(() => {
    if (!firebaseUser || !approvedUid || approvedUid !== firebaseUser.uid) return;
    let cancelled = false;
    // Every step after a wait checks this before the next request: a reply
    // for an account that is no longer the one signed in goes no further.
    const stillMine = () => !cancelled && auth.currentUser?.uid === approvedUid;

    // The profile: users/{uid} when it is an event record for this email (an
    // event account may read nothing else in users), else the lookup by email
    // as before (src/lib/eventAccount.js).
    const lookup = () => loadProfile(
      { uid: firebaseUser.uid, email: firebaseUser.email },
      // getOwn reads users/{uid} itself (dataService.users.get hides errors):
      // refused means "not an event account", no connection means try again.
      {
        getOwn: async (uid) => {
          const snap = await getDoc(doc(db, "users", uid));
          return snap.exists() ? { ...snap.data(), _docId: snap.id } : null;
        },
        getByEmail: (email) => dataService.users.getByEmail(email),
      },
    );

    (async () => {
      try {
        // Look up user in the new `users` collection by email. getByEmail
        // swallows its own errors and returns null; a permission-denied that
        // does escape means the same thing (not enrolled) and takes the same path.
        let user = null;
        try {
          user = await lookup();
        } catch (e) {
          if (e?.code !== "permission-denied") throw e;
        }
        if (!stillMine()) return;

        if (!user) {
          // First sign-in for an allow-listed account (holds slice 2): the
          // bootstrapUser callable creates users/{uid} server-side from
          // app/allowed_users/members/{email} with exactly that role, then the
          // lookup runs once more. An email that is not enrolled is refused by
          // the server (permission-denied) and lands on Access denied below.
          // The browser never writes users/{uid}.
          // A lookup that fails after it (no connection) goes to "try
          // again" below, not to Access denied.
          // Only a refusal means "not enrolled"; any other failure of the
          // callable (no connection, the function unavailable) is "try again".
          let booted = false;
          try {
            await bootstrapUser();
            booted = true;
          } catch (e) {
            console.warn("[Auth] bootstrapUser:", e?.code || e?.message || e);
            const code = typeof e?.code === "string" ? e.code.replace(/^functions\//, "") : "";
            if (code !== "permission-denied" && code !== "unauthenticated" && code !== "failed-precondition") throw e;
          }
          if (!stillMine()) return;
          if (booted) user = await lookup();
          if (!stillMine()) return;
        }

        if (!user) {
          // User exists in Firebase Auth but is not enrolled in suite.io
          // (no allow-list entry, or an unverified email).
          if (stillMine()) {
            setAuthError("Access denied. Ask your admin to add " + firebaseUser.email + " to suite.io.");
            await signOut(auth);
            setLoading(false);
          }
          return;
        }

        if (stillMine()) {
          // Equipment names from Firestore (fire-and-forget, uses fallback if
          // slow): app/equipment_config, which an event account may not read.
          if (!isEventUser(user)) loadEquipmentNames();
          setAppUser(user);
          setLoading(false);
        }
      } catch (e) {
        console.error("[Auth] Failed to resolve user:", e);
        if (stillMine()) {
          if (e?.code === "permission-denied") {
            // Hardened rules deny the users scan to non-members — same meaning
            // as "no user doc": this account isn't enrolled in suite.io.
            setAuthError("Access denied. Ask your admin to add " + firebaseUser.email + " to suite.io.");
            await signOut(auth);
          } else {
            setAuthError("Failed to load user profile. Try again.");
          }
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [firebaseUser, approvedUid]);

  const dismissAuthError = useCallback(() => setAuthError(null), []);
  const handleEnrolled = useCallback(() => {
    justEnrolled.current = true;
    setGateRun((n) => n + 1);
  }, []);
  const retryGate = useCallback(() => setGateRun((n) => n + 1), []);

  // Feedback badge on the Website tile — reload when returning to homepage.
  // Open (and doing) items in the one inbox: planner notes, plus, for an
  // admin, the Feedback tab's reports (the callable answers admins only; a
  // refusal or a failure just leaves them out of the count).
  useEffect(() => {
    if (!appUser || activeApp || displayRoute || isEventUser(appUser)) return;
    if (!(appUser.apps || []).includes("website")) return;
    let cancelled = false;
    // Checked right after the wait, before the next reads: a reply that lands
    // after another account signed in (in another tab) goes no further, even
    // before React has caught up with the change.
    const startedFor = auth.currentUser?.uid ?? null;
    const admin = appUser.role === "admin" || appUser.role === "superadmin";

    (async () => {
      const suite = admin
        ? feedbackApi.countOpen().catch(() => ({ open: 0, bugs: 0 }))
        : Promise.resolve({ open: 0, bugs: 0 });
      try {
        const sessions = await dataService.plannerSessions.loadAll();
        if (cancelled || (auth.currentUser?.uid ?? null) !== startedFor) return;
        let count = 0;
        await Promise.all(
          sessions.map(async (s) => {
            const fb = await dataService.feedback.loadForSession(s._docId);
            count += fb.filter((f) => { const st = plannerStatus(f.status); return st === "open" || st === "doing"; }).length;
          })
        );
        const s = await suite;
        if (!cancelled && (auth.currentUser?.uid ?? null) === startedFor) {
          setFeedbackCount({ open: count + (s?.open || 0), bugs: s?.bugs || 0 });
        }
      } catch (e) {
        console.error("[Badge] Failed to load feedback count:", e);
      }
    })();

    return () => { cancelled = true; };
  }, [appUser, activeApp, displayRoute]);

  const handleSignOut = useCallback(async () => {
    resetLocalSession();
    await signOut(auth);
    setAppUser(null);
    setActiveApp(null);
  }, [resetLocalSession]);

  // Idle sign-out (docs/ops/password-signin.md): this device's setting, Off
  // unless an admin turns it on from the home page. The second-display window
  // runs it too: nobody touches that window, but use of the console in any
  // other tab counts (the last use is shared), so it only signs the device out
  // when nothing on the device has been used.
  const [idleMinutes, setIdleMinutes] = useIdleMinutes();
  const handleIdleExpire = useCallback(async () => {
    setAuthError(idleSignedOutText(idleMinutes));
    await handleSignOut();
  }, [idleMinutes, handleSignOut]);
  const idle = <IdleSignOut minutes={idleMinutes} onExpire={handleIdleExpire} />;
  const isAdmin = appUser?.role === "admin" || appUser?.role === "superadmin";

  const handleOpenApp = useCallback((appId) => {
    if (eventAccount && appId !== "ingest") return;
    setActiveApp(appId);
    setActiveNav(0);
  }, [eventAccount]);

  const handleGoHome = useCallback(() => {
    setActiveApp(null);
  }, []);

  const loadingScreen = (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontFamily: font }}>
      Loading...
    </div>
  );

  // Not signed in (loading: Firebase has not said yet whether a session
  // was kept from last time).
  if (!firebaseUser) {
    return loading ? loadingScreen : <LoginScreen error={authError} onDismissError={dismissAuthError} />;
  }

  // The password sign-in check's own screens: nothing else is mounted, so
  // nothing is read, while either shows.
  if (gateStatus === "enroll") {
    return <>{idle}<AuthenticatorSetup user={firebaseUser} onEnrolled={handleEnrolled} onSignOut={handleSignOut} /></>;
  }
  if (gateStatus === "failed") {
    return <SignInCheckFailed onRetry={retryGate} onSignOut={handleSignOut} />;
  }

  // Loading state (the sign-in check, then the Suite profile)
  if (loading || !approvedUid) return loadingScreen;

  // Signed in to Firebase, but the Suite profile lookup failed (an access
  // denial signs out instead, and lands on the sign-in screen above).
  if (!appUser) {
    return <SignInCheckFailed text={authError} onRetry={retryGate} onSignOut={handleSignOut} />;
  }

  // The second display window: full screen, no Suite chrome. With no job in
  // the URL it says where to open it from.
  if (displayRoute) {
    return <><LastCaptureDisplay jobId={displayRoute.job} user={appUser} />{idle}</>;
  }

  const initials = getInitials(appUser.name || appUser.email || "");

  // The CRM's ⌘K palette lives inside CrmApp; the topbar is its sibling, so the
  // badge asks for it by event rather than by a callback threaded through the
  // whole shell. Same event CrmApp binds ⌘K to.
  const openCrmSearch = () => window.dispatchEvent(new CustomEvent(SEARCH_EVENT));

  // In-app view (an event account: Ingest and nothing else)
  if (activeApp && eventAccount && activeApp !== "ingest") {
    setActiveApp("ingest");
    return null;
  }
  if (activeApp) {
    const app = getApp(activeApp);
    if (!app) {
      setActiveApp(null);
      return null;
    }

    const appContent = activeApp === "website"
      ? <WebsiteApp activeTab={activeNav} user={appUser} />
      : activeApp === "jobs"
      ? <JobsApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} navItems={visibleNav} setActiveTab={setActiveNav} pendingConvertId={pendingConvertId} onConvertConsumed={consumeConvert} />
      : activeApp === "ingest"
      ? <IngestApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} setActiveTab={setActiveNav} onSignOut={handleSignOut} />
      : activeApp === "safeshare"
      ? <SafeShareApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} setActiveTab={setActiveNav} />
      : activeApp === "traffic"
      ? <TrafficApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} setActiveTab={setActiveNav} />
      : activeApp === "sheets"
      ? <SheetsApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} setActiveTab={setActiveNav} />
      : activeApp === "autocycle"
      ? <AutocycleApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} setActiveTab={setActiveNav} />
      : activeApp === "crm"
      ? <CrmApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} setActiveTab={setActiveNav} />
      : activeApp === "financials"
      ? <FinancialsApp activeTab={activeNav} navClickTick={navClickTick} user={appUser} navItems={visibleNav} setActiveTab={setActiveNav} pendingQboCallback={pendingQboCallback} onQboCallbackConsumed={consumeQboCallback} />
      : (
        <div style={styles.placeholder}>
          <div style={styles.placeholderLabel}>{app.name}</div>
          <p>App content loads here. Navigation lives in the top bar above.</p>
          <p style={{ marginTop: 16, fontSize: 11, color: C.muted }}>
            Click the <strong>NYC Suite</strong> button to go back home
          </p>
        </div>
      );

    return (
      <div>
        <AppTopbar
          appName={app.name}
          navItems={visibleNav}
          userInitials={initials}
          userEmail={appUser?.email}
          userName={appUser?.name}
          onGoHome={handleGoHome}
          onSignOut={handleSignOut}
          activeNav={activeNav}
          onNavChange={handleNavChange}
          onSearch={activeApp === "crm" ? openCrmSearch : undefined}
          onFeedback={eventAccount ? undefined : openFeedback}
          showWho={activeApp === "ingest"}
        />
        <div className="app-content" style={styles.appContent}>
          {appContent}
        </div>
        <Notifications />
        {!eventAccount && (
          <FeedbackTab key={approvedUid} account={approvedUid} currentAccount={currentAccount}
            where={{ app: app.id, appName: app.name, tab: visibleNav[activeNav] || null }} />
        )}
        {idle}
      </div>
    );
  }

  const badgeText = feedbackCount.open > 0
    ? `${feedbackCount.open} open${feedbackCount.bugs > 0 ? ` · ${feedbackCount.bugs} ${feedbackCount.bugs === 1 ? "bug" : "bugs"}` : ""}`
    : null;

  // Homepage
  return (
    <>
      <Homepage
        user={appUser}
        onOpenApp={handleOpenApp}
        onSignOut={handleSignOut}
        onFeedback={eventAccount ? undefined : openFeedback}
        idleMinutes={isAdmin ? idleMinutes : undefined}
        onIdleChange={isAdmin ? setIdleMinutes : undefined}
        badges={badgeText ? { website: { text: badgeText, variant: "feedback" } } : {}}
      />
      {!eventAccount && (
        <FeedbackTab key={approvedUid} account={approvedUid} currentAccount={currentAccount}
          where={{ app: null, appName: "Home", tab: null }} />
      )}
      {idle}
    </>
  );
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.split(/[\s@]+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const styles = {
  appContent: {
    padding: 24,
    maxWidth: 1200,
    margin: "0 auto",
    animation: "fadeUp 0.3s ease",
  },
  placeholder: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: 32,
    textAlign: "center",
    color: C.muted,
    fontSize: 13,
    fontWeight: 300,
  },
  placeholderLabel: {
    fontSize: 11,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.muted,
    marginBottom: 8,
  },
};
